import { produce } from 'immer';
import {
    FieldId,
    FieldValidationErrors,
    Organization,
    PersonalInfoViewModel,
    VerificationService,
    FieldContent,
    FieldRef,
    ExtendedFieldId,
    WithOrganization,
    InputSelectOnKeyDown,
    FormSelectChoice,
    Country,
    ProgramTheme,
    Locale,
    ViewModel,
} from '../../types/types';
import { validateFieldById, getFieldValidationErrors, isFormErrored } from '../../validators/validators';
import { logger } from '../logger/logger';
import { setFocus } from '../browser/inputHelpers';
import { getRefByFieldId, getRefs } from '../../refs/refs';
import { VerificationStepsEnum } from '../../types/runtimeTypes';
import { getConfiguredCountries, getAvailableLocales } from '../../../es6';
import { InjectedIntl } from 'react-intl';

export const updateViewModelOrganization = (
    organization: Organization,
    verificationService: VerificationService,
) => {
    const viewModel = verificationService.viewModel as WithOrganization;
    const nextState = produce(viewModel, (draft) => {
        draft.organization.id = organization.id;
        draft.organization.name = organization.name;
    });
    verificationService.updateViewModel(nextState as ViewModel);
};

export const updateFieldValidationErrorsByFieldId = (
    fieldId: FieldId | ExtendedFieldId,
    value: FieldContent,
    verificationService: VerificationService,
) => {
    const fieldValidationErrors = verificationService.fieldValidationErrors;
    const nextState = produce(fieldValidationErrors, (draft) => {
        if (draft[fieldId]) {
            draft[fieldId] = validateFieldById(fieldId, value);
        }
    });
    updateFieldValidationErrors(nextState, verificationService);
};

export const updateFieldValidationErrors = (fieldValidationErrors: FieldValidationErrors, verificationService: VerificationService) => {
    verificationService.updateFieldValidationErrors(fieldValidationErrors);
};

/**
 * @private
 */
export const getFieldDisplayOrderFromRefs = (): FieldId[] | ExtendedFieldId[] => {
    const refs: FieldRef[] = getRefs();
    const fieldDisplayOrder: FieldId | ExtendedFieldId[] = [];

    refs.map((refObject: FieldRef) => fieldDisplayOrder.push(refObject.fieldId));

    return fieldDisplayOrder;
};

/**
 * @private
 */
export const getFirstErroredFieldId = (
    fieldDisplayOrder: FieldId[] | ExtendedFieldId[],
    fieldValidationErrors: FieldValidationErrors,
): FieldId | ExtendedFieldId => {
    let firstErrordFieldId: FieldId | ExtendedFieldId;

    for (const field of fieldDisplayOrder) {
        if (fieldValidationErrors[field]) {
            firstErrordFieldId = field;
            return adjustFirstErroredFieldId(firstErrordFieldId);
        }
    }
    return;
};

/**
 * HD-638 - focus on year, rather than month (which is an open <select> that covers error msg)
 */
export const adjustFirstErroredFieldId = (firstErroredFieldId: FieldId | ExtendedFieldId): FieldId | ExtendedFieldId => {
    if (firstErroredFieldId === 'birthDate' || firstErroredFieldId === 'birthDateDay') {
        return 'birthDateYear';
    }
    return firstErroredFieldId;
};

/**
 * @private
 */
export const handleEmailOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const submitButtonRef: HTMLElement = getRefByFieldId('submitButton');
    if (event.key === 'Enter' && submitButtonRef) {
        submitButtonRef.click();
    }
};

/**
 * @private
 */
export const handleCountryOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();
        setFocus('organization');
    }
};

/**
 * @private
 */
export const submitForm = (viewModel: PersonalInfoViewModel, verificationService: VerificationService, step: VerificationStepsEnum) => {
    const currentFieldValidationErrors = getFieldValidationErrors(viewModel);
    const isFormValid = !isFormErrored(currentFieldValidationErrors);

    if (isFormValid) {
        const nextState = produce(viewModel, () => {});
        logger.info(`${step} submitting form`);
        verificationService.submitStep(step, nextState, verificationService.verificationResponse);
    } else {
        const fieldDisplayOrder: FieldId[] | ExtendedFieldId[] = getFieldDisplayOrderFromRefs();
        const firstErroredFieldId: FieldId | ExtendedFieldId = getFirstErroredFieldId(fieldDisplayOrder, currentFieldValidationErrors);
        updateFieldValidationErrors(currentFieldValidationErrors, verificationService);
        setFocus(firstErroredFieldId);
    }
};

/**
 * @private
 */
export const getAvailableCountryChoices = (programTheme: ProgramTheme, intl: InjectedIntl): FormSelectChoice<Country, string>[] => {
    const availableCountries: Country[] = getConfiguredCountries(programTheme);
    return availableCountries
        .map(countryCode => ({
            value: countryCode,
            label: intl.formatMessage({
                id: `countries.${countryCode}`,
                defaultMessage: countryCode,
            }),
        }));
};

/**
 * @private
 */
export const getAvailableLocaleChoices = (programTheme: ProgramTheme, intl: InjectedIntl): FormSelectChoice<Locale, string>[] => {
    const availableLocales: Locale[] = getAvailableLocales(programTheme);
    return availableLocales
        .map(locale => ({
            value: locale,
            label: intl.formatMessage({
                id: `locales.${locale}`,
                defaultMessage: locale,
            }),
        }));
};

/**
 * @private
 */
export const getDefaultCountryChoice = (countryChoices: FormSelectChoice<Country, string>[]) : FormSelectChoice<Country, string> => {
    if (countryChoices.length > 0) {
        return countryChoices[0];
    }
    return { value: 'US', label: 'United States' };
};
